import React from 'react';
import { ThumbsUp, DollarSign, Car, Building2, TrendingUp, Lock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useScrollReveal } from '../hooks/useAnimations';
import { Reveal, Words } from './motion/Reveal';

const features = [
  {
    icon: <ThumbsUp className="w-7 h-7" strokeWidth={2} />,
    titleFr: 'TAUX D\'APPROBATION',
    titleBoldFr: 'ÉLEVÉ',
    titleEn: 'HIGH APPROVAL',
    titleBoldEn: 'RATE',
    descFr: 'Nous analysons chaque dossier individuellement. Grâce à notre réseau de prêteurs, nous trouvons des options adaptées à la majorité des situations financières.',
    descEn: 'We analyze each file individually. Thanks to our lender network, we find options suited to most financial situations.'
  },
  {
    icon: <DollarSign className="w-7 h-7" strokeWidth={2} />,
    titleFr: 'FINANCEMENT',
    titleBoldFr: 'FLEXIBLE',
    titleEn: 'FLEXIBLE',
    titleBoldEn: 'FINANCING',
    descFr: 'Selon votre dossier, des options sans mise de fonds initiale peuvent être disponibles. Notre objectif est de vous offrir un plan de paiement réaliste.',
    descEn: 'Depending on your file, options without an initial down payment may be available. Our goal is to offer a realistic payment plan.'
  },
  {
    icon: <Car className="w-7 h-7" strokeWidth={2} />,
    titleFr: 'VÉHICULES',
    titleBoldFr: 'INSPECTÉS',
    titleEn: 'INSPECTED',
    titleBoldEn: 'VEHICLES',
    descFr: 'Accédez à une sélection de véhicules d\'occasion vérifiés et prêts pour la route, disponibles via notre réseau de concessionnaires partenaires.',
    descEn: 'Access a selection of verified pre-owned vehicles ready for the road, available through our partner dealer network.'
  },
  {
    icon: <Building2 className="w-7 h-7" strokeWidth={2} />,
    titleFr: 'PARTENAIRES',
    titleBoldFr: 'FINANCIERS',
    titleEn: 'FINANCIAL',
    titleBoldEn: 'PARTNERS',
    descFr: 'Nous collaborons avec des institutions financières reconnues pour vous obtenir les meilleures conditions possibles sur votre prêt automobile.',
    descEn: 'We work with recognized financial institutions to get you the best possible terms on your auto loan.'
  },
  {
    icon: <TrendingUp className="w-7 h-7" strokeWidth={2} />,
    titleFr: 'REBÂTIR VOTRE',
    titleBoldFr: 'CRÉDIT',
    titleEn: 'REBUILD YOUR',
    titleBoldEn: 'CREDIT',
    descFr: 'Chaque paiement effectué à temps contribue à améliorer votre dossier de crédit. Un financement structuré est le premier pas vers un nouveau départ.',
    descEn: 'Every payment made on time helps improve your credit file. Structured financing is the first step toward a fresh start.'
  },
  {
    icon: <Lock className="w-7 h-7" strokeWidth={2} />,
    titleFr: 'DOSSIER',
    titleBoldFr: 'CONFIDENTIEL',
    titleEn: '100%',
    titleBoldEn: 'CONFIDENTIAL',
    descFr: 'Votre demande de financement est traitée de manière sécuritaire et confidentielle. Vos informations personnelles sont protégées en tout temps.',
    descEn: 'Your financing application is processed securely and confidentially. Your personal information is protected at all times.'
  }
];

const FeatureGrid = () => {
  const { language } = useLanguage();
  const ref = useScrollReveal();

  return (
    <section ref={ref} className="reveal py-14 md:py-20 bg-[#f3f4f0]" data-testid="feature-grid">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs font-bold text-[#1a5c3b] uppercase tracking-[0.2em] mb-4">
            {language === 'en' ? 'Our advantages' : 'Nos avantages'}
          </p>
          <Words parts={[[language === 'en' ? 'Why choose Credit Auto Reset?' : 'Pourquoi choisir Crédit Auto Reset\u00a0?']]} className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0f172a]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
          {features.map((f, i) => (
            <Reveal key={i} delay={(i % 3) * 150} className="text-center group" data-testid={`feature-item-${i}`}>
              <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-[#1a5c3b] flex items-center justify-center text-white shadow-lg shadow-[#1a5c3b]/20 group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-[#1a5c3b]/30 transition-all duration-300">
                {f.icon}
              </div>
              <h3 className="text-lg md:text-xl font-extrabold text-[#0f172a] uppercase tracking-wide mb-3">
                {language === 'en' ? f.titleEn : f.titleFr}{' '}
                <span className="text-[#1a5c3b]">{language === 'en' ? f.titleBoldEn : f.titleBoldFr}</span>
              </h3>
              <p className="text-sm md:text-base text-slate-500 leading-relaxed max-w-[300px] mx-auto">
                {language === 'en' ? f.descEn : f.descFr}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeatureGrid;
